import React from 'react'
import { useTranslation } from 'i18n'
import LegacyRetinaImage from 'components/LegacyRetinaImage'
import SlideArrowLeft from 'images/inline/slide-arrow-left.svg'
import ArrowDownCircle from 'images/inline/arrow-down-circle.svg'
import useEmblaCarousel from 'embla-carousel-react'
import { DropdownButton } from 'steps/referral/parts/DropdownButton2'
import { usePrevNextButtons } from 'components/scrollable/EmblaCarouselArrowButtons'
import { useDotButton } from 'components/scrollable/EmblaCarouselDotButton'
import { usePollColors } from './use-poll-colors'

export const ProductDropdownButton = ({
  label,
  links,
  color,
  labelStyle,
  onLinkClick,
  onDropdownOpen,
  onDropdownLinkClick,
}) => {
  const { t } = useTranslation('poll')
  if (!links?.length) return null

  if (links.length === 1) {
    return (
      <a
        href={links[0].link}
        target="_blank"
        rel="noopener noreferrer"
        style={labelStyle}
        onClick={() => onLinkClick?.(links[0].link, links[0].label)}
      >
        {label || links[0].label || t('shopNow')}
      </a>
    )
  }

  return (
    <DropdownButton
      text={
        <span style={labelStyle}>
          {label || t('shopNow')}
          <ArrowDownCircle />
        </span>
      }
      items={links}
      color={color}
      noBoxShadow
      showArrow={false}
      onDropdownOpen={onDropdownOpen}
      onClick={(_, text, url) => onDropdownLinkClick?.(url, text)}
    />
  )
}

export const PollProducts = ({
  title,
  titleStyle,
  products,
  activeColor,
  question,
  onAnswerDropdownClick,
  onAnswerLinkClick,
  poll,
}) => {
  const { t } = useTranslation('poll')
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
  })

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi)

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons({ emblaApi })

  const { getProductColors } = usePollColors(poll)

  return (
    <div className="poll-products">
      <h3 style={titleStyle}>{title}</h3>

      <div ref={emblaRef} className="embla">
        <div className="embla__container">
          {products.map(product => {
            const links = product.dropdownButton?.links?.map(link => ({
              ...link,
              link: link.url,
            }))

            const productColors = getProductColors(product)

            return (
              <div
                key={`poll-product-${product.id}`}
                className="embla__slide poll-product-slide"
                style={{
                  '--poll-product-button-bg-color':
                    productColors.dropdownButtonBgColor,
                  '--poll-product-button-text-color':
                    productColors.dropdownButtonTextColor,
                  '--poll-product-button-bg-hover-color':
                    productColors.dropdownButtonBgHoverColor,
                }}
              >
                <LegacyRetinaImage image={product.image} />
                {product.puckDropdownContent
                  ? product.puckDropdownContent
                  : product.dropdownButton && (
                      <ProductDropdownButton
                        label={product.dropdownButton.label}
                        links={links}
                        color={productColors.dropdownButtonBgColor}
                        onLinkClick={(link, linkTitle) =>
                          onAnswerLinkClick({
                            questionId: question.id,
                            productId: product.id,
                            link,
                            linkTitle,
                          })
                        }
                        onDropdownOpen={() =>
                          onAnswerDropdownClick({
                            questionId: question.id,
                            productId: product.id,
                            dropdownTitle: product.dropdownButton.label,
                          })
                        }
                        onDropdownLinkClick={(link, linkTitle) =>
                          onAnswerLinkClick({
                            questionId: question.id,
                            productId: product.id,
                            link,
                            linkTitle,
                          })
                        }
                      />
                    )}
              </div>
            )
          })}
        </div>
      </div>

      <div
        className="poll-products-slide-navigation"
        style={{ color: activeColor }}
      >
        <SlideArrowLeft
          onClick={onPrevButtonClick}
          className={prevBtnDisabled ? 'disabled' : ''}
        />
        <div className="slide-dots">
          {Array.from({
            length: scrollSnaps.length,
          }).map((_, idx) => {
            return (
              <button
                key={`poll-product-slide-dot-${idx}`}
                onClick={() => onDotButtonClick(idx)}
                className={
                  'slide-dot' + (selectedIndex === idx ? ' active' : '')
                }
                aria-label={t('slideAriaLabel', { number: idx + 1 })}
              />
            )
          })}
        </div>
        <SlideArrowLeft
          onClick={onNextButtonClick}
          style={{ rotate: '180deg' }}
          className={nextBtnDisabled ? 'disabled' : ''}
        />
      </div>
    </div>
  )
}
